/**
 * @file Synchronisation SimView -> objets Three : rigs de chats (pool par skin, arme tenue), cibles instanciees, projectiles ; interpolation prev->courant.
 */
import { Color, Vector3 } from 'three';
import type { Object3D, Quaternion, Scene } from 'three';
import type { Matrix4 } from 'three';
import { CONFIG, DEFAULT_SKIN_ID } from '@config/index';
import type { ConfigIndex } from '@config/index';
import type { SimView } from '@core/index';
import type { CatFactory } from '../factories/CatFactory';
import type { GunFactory, GunModel } from '../factories/GunFactory';
import { PartBatch } from '../gpu/PartBatch';
import type { PartHandle } from '../gpu/PartBatch';
import { animateCat } from '../rig/CatAnimator';
import { newAnimState } from '../rig/CatRig';
import type { CatAnimState, CatRig } from '../rig/CatRig';
import type { PlacesFx } from '../fx/PlacesFx';
import type { GiantTrees } from './GiantTrees';
import type { GroundRenderer } from './GroundRenderer';
import { HealthBars } from './HealthBars';
import { HeroMarker } from './HeroMarker';
import type { InstancedTargets, TargetFrame } from './InstancedTargets';
import type { ProjectileFrame, ProjectileRenderer } from './ProjectileRenderer';

interface CatView {
  rig: CatRig;
  anim: CatAnimState;
  lastX: number;
  lastY: number;
  /** Vitesse lissee normalisee (0..1) pour choisir l'amplitude de marche. */
  speed01: number;
  weaponUid: string | null;
  /** Groupe de l'arme actuellement attachee (pour la retirer au changement d'arme). */
  gun: Object3D | null;
  kickback: number;
  /** Fondu 0..1 de la teinte « a l'ombre » (sous la couronne d'un arbre geant). */
  shade: number;
  /** Instances du lot : une par os du chat (matrice monde de l'os) et une par piece de l'arme (matrice du groupe). */
  bodyParts: Array<{ bone: Object3D; handle: PartHandle }>;
  gunParts: Array<{ handle: PartHandle; shimmer: boolean; phase: number }>;
  gunMatrix: Matrix4 | null;
}

/** Un autre joueur du hub (rendu seul : le core ne le connait pas). Coordonnees du core (x droite, y vers le fond). */
export interface RemoteCat {
  id: string;
  x: number;
  y: number;
  rot: number;
  moving: boolean;
  skinId: string;
  /** Arme tenue (id de definition ; null = aucune) et sa visee (rad) ; rangee = tenue sur le cote. */
  weaponDefId: string | null;
  aim: number;
  stowed: boolean;
  /** Aura portee (dessinee par GameRenderer / RemoteAuras ; null = aucune). */
  auraId?: string | null;
}

interface RemoteView {
  cv: CatView;
  x: number;
  y: number;
  tx: number;
  ty: number;
  rot: number;
  aim: number;
  stowed: boolean;
  moving: boolean;
  live: boolean;
}

/** Instances simultanees maximales du lot des chats (<= 5 chats x (pieces du corps + pieces d'arme) + les autres joueurs du hub, armes comprises). */
const MAX_CAT_INSTANCES = 1100;
/** Autres joueurs du hub affiches au plus (les plus proches d'abord, choisis par l'appelant). */
export const MAX_REMOTE_CATS = 24;
/** Vitesse de rattrapage (1/s) d'un joueur distant vers la derniere position recue (instantanes a 10 Hz). */
const REMOTE_FOLLOW = 14;

/** Rayon de l'ombre d'un chat et plafond de celle d'une cible. */
const CAT_SHADOW_RADIUS = 0.8;
const TARGET_SHADOW_SCALE = 0.85;
const TARGET_SHADOW_MAX = 2.6;
/** Lissage de la vitesse affichee (1/s). */
const SPEED_SMOOTHING = 10;
/** Echelle d'affichage des chats : lisibilite sur un ecran portrait (fitWidth 24 u), purement visuelle. */
const CAT_VISUAL_SCALE = 1.55;
/** Sur le chemin du Main, les arbres sont affiches plus gros (rangees denses comme l'original), purement visuel. */
const PATH_TREE_SCALE = 1.35;
/** Barre de vie : hauteur au-dessus du sol (par famille, x echelle de la cible) et largeur. */
const TREE_BAR_HEIGHT = 2.1;
const OTHER_BAR_HEIGHT = 1.4;
const BAR_LIFT = 0.55;
const BAR_BASE_WIDTH = 0.8;
const BAR_WIDTH_PER_SQRT = 0.6;

/**
 * sync(view, alpha, dt, time) a chaque frame :
 *  - Position interpolee p = prev + (cur - prev) * alpha, conversion core (x, y) -> Three (x, h, -y).
 *  - Chats : un CatView par EntityId (pool de rigs par skinId) ; l'arme (cat.weaponUid) est construite par GunFactory et attachee a
 *    weaponMount ; animateCat() reçoit l'etat du core ; les vues des chats disparus retournent au pool.
 *  - Cibles / projectiles : un seul parcours des entites, remplissage de cadres reutilises (aucune allocation par frame).
 *  - Ombres de contact : chats r 0.5, cibles collider.radius.
 */
export class EntityViews {
  private readonly batch: PartBatch;
  private readonly bars: HealthBars;
  /** Anneau au sol sous le chat du joueur (lisibilite parmi les arbres / les autres joueurs du hub). */
  private readonly marker: HeroMarker;
  private readonly cats = new Map<number, CatView>();
  private readonly remote = new Map<string, RemoteView>();
  private readonly rigPool = new Map<string, CatRig[]>();
  private readonly seen = new Set<number>();
  private readonly tmp = new Vector3();
  /** Positions monde (Three) des chats [x0, z0, x1, z1...] de la frame : fondu des couronnes des arbres geants. */
  private readonly catPos = new Float32Array(32);
  private catCount = 0;
  /** Decalage au sol par unite de hauteur de la ligne de visee et sinus du pitch (voir GiantTrees.end). */
  private shiftX = 0;
  private shiftZ = 1;
  private sinPitch = 0.77;
  private readonly shadeColor = new Color();
  private readonly baseSpeed = CONFIG.balance.combat.baseMoveSpeed;
  private readonly targetFrame: Omit<TargetFrame, 'def'> & { def: TargetFrame['def'] | null } = {
    id: 0,
    def: null,
    variant: 0,
    x: 0,
    y: 0,
    rot: 0,
    scale: 1,
    hitFlash: 0,
    dying: 0,
    shootable: true,
    time: 0,
  };
  private readonly projFrame: ProjectileFrame = {
    behavior: 'single',
    kind: 'bullet',
    x: 0,
    y: 0,
    rot: 0,
    arcT: 0,
    arcHeight: 0,
    x1: 0,
    y1: 0,
    width: 0,
    fade: 1,
    crit: false,
  };

  constructor(
    private readonly scene: Scene,
    private readonly index: ConfigIndex,
    private readonly catFactory: CatFactory,
    private readonly gunFactory: GunFactory,
    private readonly targets: InstancedTargets,
    private readonly projectiles: ProjectileRenderer,
    private readonly ground: GroundRenderer,
    private readonly giants: GiantTrees,
    private readonly places: PlacesFx,
    /** Fourni par l'app : uid d'arme -> defId (lecture de PlayerData). */
    private readonly weaponDefOf: (uid: string) => string | null,
  ) {
    this.batch = new PartBatch(scene, catFactory.kit, MAX_CAT_INSTANCES, 64000);
    this.bars = new HealthBars(scene, catFactory.kit);
    this.marker = new HeroMarker(scene);
  }

  /** Orientation de la camera : les barres de vie lui font face. */
  setCamera(q: Quaternion): void {
    // direction de visee : un point a la hauteur h se projette au sol en p + h * (fx, fz) / -fy
    this.tmp.set(0, 0, -1).applyQuaternion(q);
    const down = Math.max(0.2, -this.tmp.y);
    this.shiftX = this.tmp.x / down;
    this.shiftZ = this.tmp.z / down;
    this.sinPitch = down;
    this.bars.setCamera(q);
    this.projectiles.setCamera(q);
  }

  sync(view: SimView, alpha: number, dt: number, time: number): void {
    const a = Math.min(1, Math.max(0, alpha));
    this.ground.beginShadows();

    // ---- chats
    const stowed = view.weaponStowed;
    const doubleShadow = view.season === 'spring' || view.season === 'summer';
    this.catCount = 0;
    this.seen.clear();
    this.marker.hide();
    const leaderId = view.catIds[0];
    for (const id of view.catIds) {
      const e = view.entity(id);
      if (!e || !e.cat || !e.alive) continue;
      this.seen.add(id);
      const c = e.cat;
      let cv = this.cats.get(id);
      if (cv && cv.rig.skinId !== c.skinId) {
        this.recycle(cv);
        this.cats.delete(id);
        cv = undefined;
      }
      const t = e.transform;
      const x = t.prevX + (t.x - t.prevX) * a;
      const y = t.prevY + (t.y - t.prevY) * a;
      if (!cv) {
        const rig = this.acquireRig(c.skinId);
        const anim = newAnimState(((id * 0.6180339) % 1 + 1) % 1);
        anim.lastShotSeq = c.shotSeq;
        cv = { rig, anim, lastX: x, lastY: y, speed01: 0, weaponUid: null, gun: null, kickback: CONFIG.behaviors.single.kickback, shade: 0, bodyParts: [], gunParts: [], gunMatrix: null };
        for (const part of this.catFactory.partsOf(rig)) {
          this.batch.register(part.key, () => ({ fill: part.fill, hull: part.hull }));
          const handle = this.batch.add(part.key);
          if (handle) cv.bodyParts.push({ bone: part.bone, handle });
        }
        this.cats.set(id, cv);
        this.setWeapon(cv, c.weaponUid);
      } else if (cv.weaponUid !== c.weaponUid) {
        this.setWeapon(cv, c.weaponUid);
      }

      const inst = dt > 0 ? Math.hypot(x - cv.lastX, y - cv.lastY) / dt : 0;
      const target = Math.min(1, inst / this.baseSpeed);
      cv.speed01 += (target - cv.speed01) * (1 - Math.exp(-SPEED_SMOOTHING * dt));
      cv.lastX = x;
      cv.lastY = y;

      cv.rig.bones.root.position.set(x, 0, -y);
      cv.rig.bones.root.scale.setScalar(CAT_VISUAL_SCALE);
      animateCat(cv.rig, cv.anim, {
        moving: c.moving,
        speed01: cv.speed01,
        heading: t.rot,
        aimAngle: c.aimAngle,
        shotSeq: c.shotSeq,
        kickback: cv.kickback,
        stowed,
        time,
        dt,
      });
      // a l'ombre d'un arbre geant : leger fondu vers une teinte plus sombre et froide
      const inShade = this.giants.isShaded(x, -y) ? 1 : 0;
      cv.shade += (inShade - cv.shade) * (1 - Math.exp(-8 * Math.max(0, dt)));
      if (cv.shade < 0.004) cv.shade = 0;
      if (this.catCount < 16) {
        this.catPos[this.catCount * 2] = x;
        this.catPos[this.catCount * 2 + 1] = -y;
        this.catCount++;
      }
      this.pushParts(cv);
      this.ground.drawShadow(x, y, CAT_SHADOW_RADIUS);
      if (id === leaderId) this.marker.show(x, y);
    }
    for (const [id, cv] of this.cats) {
      if (this.seen.has(id)) continue;
      this.recycle(cv);
      this.cats.delete(id);
    }
    this.syncRemote(dt, time);

    // ---- cibles + projectiles
    this.bars.begin();
    this.targets.begin();
    this.giants.begin();
    this.places.begin();
    this.projectiles.begin(time);
    const tf = this.targetFrame;
    const pf = this.projFrame;
    const pathTrees = view.arena.shape === 'corridor';
    for (const e of view.entities()) {
      if (e.kind === 'target') {
        const tg = e.target;
        const vis = e.visual;
        if (!tg || !vis) continue;
        const def = this.index.target(tg.defId);
        const t = e.transform;
        const x = t.prevX + (t.x - t.prevX) * a;
        const y = t.prevY + (t.y - t.prevY) * a;
        tf.id = e.id;
        tf.def = def;
        tf.variant = tg.variant;
        tf.x = x;
        tf.y = y;
        tf.rot = t.rot;
        // l'entite porte def.visual.scale (x1.6 boss) alors que les geometries du prototype l'integrent deja
        tf.scale = (def.visual.scale > 0 ? vis.scale / def.visual.scale : vis.scale) * (pathTrees && tg.family === 'tree' ? PATH_TREE_SCALE : 1);
        tf.hitFlash = vis.hitFlash;
        tf.dying = vis.dying;
        tf.shootable = tg.shootable;
        tf.time = time;
        const canopy = def.visual.canopyRadius;
        if (canopy !== undefined) {
          // arbre geant : objet dedie (couronne translucide) + ombre douce au sol (double disque au printemps / ete)
          const radius = canopy * def.visual.scale * tf.scale;
          this.giants.draw(tf as TargetFrame, radius);
          this.ground.drawShadow(x, y, radius);
          if (doubleShadow) this.ground.drawShadow(x, y, radius * 0.66);
        } else {
          this.targets.draw(tf as TargetFrame);
          if (def.visual.theme === 'house') {
            if (vis.dying > 0) this.places.collapseHouse(e.id, x, y, tf.rot, tg.variant, def.visual.scale * tf.scale, def.visual.palette);
            else this.places.house(x, y, tf.rot, tg.variant, def.visual.scale * tf.scale);
          }
          else if (def.visual.theme === 'lake') this.places.lake(e.id, x, y, def.visual.scale * tf.scale);
        }
        // barre de vie : uniquement pour les destructibles DEJA TOUCHES et encore vivants
        const hl = e.health;
        if (hl && tg.destructible && vis.dying <= 0) {
          const ratio = hl.hitsLeft !== undefined && def.harvest ? hl.hitsLeft / def.harvest.hits : hl.hp.div(hl.max).toNumber();
          if (ratio < 0.999) {
            const total = def.visual.scale * tf.scale;
            const high = tg.family === 'tree' ? TREE_BAR_HEIGHT : OTHER_BAR_HEIGHT;
            this.bars.draw(x, y, high * total + BAR_LIFT, ratio, BAR_BASE_WIDTH + BAR_WIDTH_PER_SQRT * Math.sqrt(total));
          }
        }
        // un lac est une surface au ras du sol : aucune ombre de contact par-dessus l'eau
        if (vis.dying <= 0 && e.collider && def.visual.theme !== 'lake') this.ground.drawShadow(x, y, Math.min(TARGET_SHADOW_MAX, Math.max(0.3, e.collider.radius * TARGET_SHADOW_SCALE)));
      } else if (e.kind === 'projectile') {
        const p = e.projectile;
        if (!p || !e.alive) continue;
        const t = e.transform;
        pf.behavior = p.behavior;
        pf.kind = p.kind;
        pf.x = t.prevX + (t.x - t.prevX) * a;
        pf.y = t.prevY + (t.y - t.prevY) * a;
        pf.rot = Math.atan2(p.vy, p.vx);
        pf.arcT = p.arc ? p.arc.t : 0;
        pf.arcHeight = p.arc ? p.arc.height : 0;
        pf.crit = p.crit;
        if (p.kind === 'beam') {
          const bp = CONFIG.behaviors[p.behavior];
          pf.x = t.x;
          pf.y = t.y;
          pf.x1 = p.beamEnd ? p.beamEnd.x : t.x;
          pf.y1 = p.beamEnd ? p.beamEnd.y : t.y;
          pf.width = bp.beamWidth;
          pf.fade = bp.beamVisibleSeconds > 0 ? Math.min(1, Math.max(0, p.lifeLeft / bp.beamVisibleSeconds)) : 1;
        }
        this.projectiles.draw(pf);
      }
    }
    this.targets.end();
    this.giants.end(dt, this.catPos, this.catCount, this.shiftX, this.shiftZ, this.sinPitch);
    this.bars.end();
    this.projectiles.end();
    this.ground.endShadows();
  }

  /** Autres joueurs du hub : la liste remplace la precedente (ceux qui manquent disparaissent). Appele a chaque instantane reseau. */
  setRemoteCats(list: readonly RemoteCat[]): void {
    for (const r of this.remote.values()) r.live = false;
    for (const p of list.slice(0, MAX_REMOTE_CATS)) {
      let r = this.remote.get(p.id);
      if (r && r.cv.rig.skinId !== p.skinId) {
        this.recycle(r.cv);
        this.remote.delete(p.id);
        r = undefined;
      }
      if (!r) {
        const cv = this.newCatView(p.skinId, p.x, p.y, p.id);
        r = { cv, x: p.x, y: p.y, tx: p.x, ty: p.y, rot: p.rot, aim: p.aim, stowed: p.stowed, moving: p.moving, live: true };
        this.remote.set(p.id, r);
      }
      const key = p.weaponDefId === null ? null : `def:${p.weaponDefId}`;
      if (r.cv.weaponUid !== key) this.setWeaponDef(r.cv, key, p.weaponDefId);
      r.tx = p.x;
      r.ty = p.y;
      r.rot = p.rot;
      r.aim = p.aim;
      r.stowed = p.stowed;
      r.moving = p.moving;
      r.live = true;
    }
  }

  private newCatView(skinId: string, x: number, y: number, seed: string | number): CatView {
    const rig = this.acquireRig(skinId);
    const h = typeof seed === 'number' ? seed : [...seed].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) >>> 0, 7);
    const anim = newAnimState((((h * 0.6180339) % 1) + 1) % 1);
    const cv: CatView = { rig, anim, lastX: x, lastY: y, speed01: 0, weaponUid: null, gun: null, kickback: CONFIG.behaviors.single.kickback, shade: 0, bodyParts: [], gunParts: [], gunMatrix: null };
    for (const part of this.catFactory.partsOf(rig)) {
      this.batch.register(part.key, () => ({ fill: part.fill, hull: part.hull }));
      const handle = this.batch.add(part.key);
      if (handle) cv.bodyParts.push({ bone: part.bone, handle });
    }
    return cv;
  }

  /** Positions affichees (lissees) des joueurs distants, coordonnees du core : auras distantes, etiquettes. */
  forEachRemote(fn: (id: string, x: number, y: number) => void): void {
    for (const [id, r] of this.remote) if (r.live) fn(id, r.x, r.y);
  }

  /** Dessine les joueurs distants : rattrapage lisse vers la derniere position, marche, ombre ; arme tenue (visee / rangee recues). */
  private syncRemote(dt: number, time: number): void {
    const k = 1 - Math.exp(-REMOTE_FOLLOW * Math.max(0, dt));
    for (const [id, r] of this.remote) {
      if (!r.live) {
        this.recycle(r.cv);
        this.remote.delete(id);
        continue;
      }
      // trop loin (teleportation, premier instantane) : on saute directement
      if (Math.hypot(r.tx - r.x, r.ty - r.y) > 12) {
        r.x = r.tx;
        r.y = r.ty;
        r.cv.lastX = r.x;
        r.cv.lastY = r.y;
      } else {
        r.x += (r.tx - r.x) * k;
        r.y += (r.ty - r.y) * k;
      }
      const cv = r.cv;
      const inst = dt > 0 ? Math.hypot(r.x - cv.lastX, r.y - cv.lastY) / dt : 0;
      const target = Math.min(1, inst / this.baseSpeed);
      cv.speed01 += (target - cv.speed01) * (1 - Math.exp(-SPEED_SMOOTHING * dt));
      cv.lastX = r.x;
      cv.lastY = r.y;
      cv.rig.bones.root.position.set(r.x, 0, -r.y);
      cv.rig.bones.root.scale.setScalar(CAT_VISUAL_SCALE);
      animateCat(cv.rig, cv.anim, { moving: r.moving, speed01: cv.speed01, heading: r.rot, aimAngle: r.aim, shotSeq: cv.anim.lastShotSeq, kickback: cv.kickback, stowed: r.stowed, time, dt });
      const inShade = this.giants.isShaded(r.x, -r.y) ? 1 : 0;
      cv.shade += (inShade - cv.shade) * (1 - Math.exp(-8 * Math.max(0, dt)));
      if (cv.shade < 0.004) cv.shade = 0;
      if (this.catCount < 16) {
        this.catPos[this.catCount * 2] = r.x;
        this.catPos[this.catCount * 2 + 1] = -r.y;
        this.catCount++;
      }
      this.pushParts(cv);
      this.ground.drawShadow(r.x, r.y, CAT_SHADOW_RADIUS);
    }
  }

  /** Position monde (Three) du muzzle du chat (ou de son ancrage d'arme / de son corps si desarme). Objet reutilise. */
  muzzleWorld(catId: number): { x: number; y: number; z: number } | null {
    const cv = this.cats.get(catId);
    if (!cv) return null;
    const o = cv.rig.muzzle ?? cv.rig.bones.weaponMount;
    o.getWorldPosition(this.tmp);
    return this.tmp;
  }

  dispose(): void {
    for (const cv of this.cats.values()) this.catFactory.release(cv.rig);
    for (const r of this.remote.values()) this.catFactory.release(r.cv.rig);
    this.remote.clear();
    this.batch.dispose();
    this.cats.clear();
    for (const list of this.rigPool.values()) for (const rig of list) this.catFactory.release(rig);
    this.rigPool.clear();
    this.targets.dispose();
    this.giants.dispose();
    this.bars.dispose();
    this.marker.dispose();
    this.projectiles.dispose();
  }

  // ------------------------------------------------------------------------------------------------------------------

  private acquireRig(skinId: string): CatRig {
    const pooled = this.rigPool.get(skinId)?.pop();
    if (pooled) return pooled;
    let skin;
    try {
      skin = this.index.skin(skinId);
    } catch {
      skin = this.index.skin(DEFAULT_SKIN_ID);
    }
    return this.catFactory.create(skin);
  }

  /** Matrices monde des os et de l'arme -> instances du lot (apres animateCat). */
  private pushParts(cv: CatView): void {
    cv.rig.bones.root.updateMatrixWorld(true);
    if (cv.shade > 0) {
      this.shadeColor.setRGB(1 - 0.22 * cv.shade, 1 - 0.15 * cv.shade, 1 - 0.04 * cv.shade);
      for (const p of cv.bodyParts) this.batch.set(p.handle, p.bone.matrixWorld, this.shadeColor);
    } else {
      for (const p of cv.bodyParts) this.batch.set(p.handle, p.bone.matrixWorld);
    }
    const gm = cv.gunMatrix;
    if (!gm) return;
    for (const p of cv.gunParts) this.batch.set(p.handle, gm, p.shimmer ? this.gunFactory.shimmerColors[p.phase] : undefined);
  }

  private clearGunParts(cv: CatView): void {
    for (const p of cv.gunParts) this.batch.remove(p.handle);
    cv.gunParts.length = 0;
    cv.gunMatrix = null;
  }

  /** Retire la vue du monde et remet son rig (sans arme) dans le pool de son skin. */
  private recycle(cv: CatView): void {
    for (const p of cv.bodyParts) this.batch.remove(p.handle);
    cv.bodyParts.length = 0;
    this.clearGunParts(cv);
    this.catFactory.release(cv.rig);
    cv.gun = null;
    let list = this.rigPool.get(cv.rig.skinId);
    if (!list) {
      list = [];
      this.rigPool.set(cv.rig.skinId, list);
    }
    list.push(cv.rig);
  }

  private setWeapon(cv: CatView, uid: string | null): void {
    this.setWeaponDef(cv, uid, uid ? this.weaponDefOf(uid) : null);
  }

  /** Arme d'un chat : `key` identifie l'arme courante (uid local ou `def:<id>` distant), `defId` la definition a construire. */
  private setWeaponDef(cv: CatView, key: string | null, defId: string | null): void {
    const rig = cv.rig;
    if (cv.gun) cv.gun.removeFromParent();
    cv.gun = null;
    this.clearGunParts(cv);
    rig.muzzle = null;
    rig.weaponDefId = null;
    cv.weaponUid = key;
    cv.kickback = CONFIG.behaviors.single.kickback;
    if (!key || !defId) return;
    const def = this.index.weapon(defId);
    const model = this.gunFactory.create(def, this.index.grade(def.grade));
    rig.bones.weaponMount.add(model.group);
    rig.muzzle = model.muzzle;
    rig.weaponDefId = def.id;
    cv.gun = model.group;
    cv.gunMatrix = model.group.matrixWorld;
    this.addGunParts(cv, model);
    cv.kickback = CONFIG.behaviors[def.behavior].kickback;
  }

  private addGunParts(cv: CatView, model: GunModel): void {
    for (const part of model.parts) {
      this.batch.register(part.key, () => ({ fill: part.fill, hull: part.hull }));
      const handle = this.batch.add(part.key);
      if (handle) cv.gunParts.push({ handle, shimmer: part.shimmer, phase: part.phase });
    }
  }
}
